import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle, AlertCircle, Lightbulb } from "lucide-react";
import GradeCard from "./GradeCard";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip
} from "recharts";

const gradeColors = {
  A: "#10B981",
  B: "#84CC16",
  C: "#EAB308",
  D: "#F97316",
  F: "#EF4444"
};

const statusColors = {
  Excellent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Good: "bg-lime-100 text-lime-700 border-lime-200",
  "Needs Improvement": "bg-yellow-100 text-yellow-700 border-yellow-200",
  Poor: "bg-orange-100 text-orange-700 border-orange-200",
  Critical: "bg-red-100 text-red-700 border-red-200"
};

const DetailItem = ({ label, value, color }) => {
  // Handle boolean values
  if (typeof value === "boolean") {
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
        <span className="text-slate-600 text-sm">{label}</span>
        {value ? (
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <span className="text-xs font-medium text-emerald-600">Active</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <XCircle className="h-5 w-5 text-red-400" />
            <span className="text-xs font-medium text-red-500">Missing</span>
          </div>
        )}
      </div>
    );
  }
  
  // Handle numeric values
  if (typeof value === "number") {
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
        <span className="text-slate-600 text-sm">{label}</span>
        <span className="font-semibold text-slate-900">{value.toLocaleString("en-US")}</span>
      </div>
    );
  }
  
  // Handle string values
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-600 text-sm">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
};

const formatLabel = (key) => {
  return key
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const SectionCard = ({ id, title, icon: Icon, data, color = "#6366F1", gradeView = "operations" }) => {
  const currentGrade = gradeView === "admin" ? data.grade : 
                       gradeView === "operations" ? data.grade_operations : data.grade;
  const statusColor = statusColors[data.status] || "bg-slate-100 text-slate-700 border-slate-200";
  
  // Generate mini chart data (simulated trend)
  const trendData = [
    { name: 'W1', value: Math.max(0, data.score - 15 + Math.random() * 10) },
    { name: 'W2', value: Math.max(0, data.score - 10 + Math.random() * 10) },
    { name: 'W3', value: Math.max(0, data.score - 5 + Math.random() * 10) },
    { name: 'W4', value: data.score },
  ];

  return (
    <section id={id} className="report-section" data-testid={`section-${id}`}>
      <Card className="border-slate-200 overflow-hidden">
        {/* Colored Top Bar */}
        <div className="h-1.5" style={{ backgroundColor: color }}></div>
        
        <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div 
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${color}15` }}
              >
                <Icon className="h-5 w-5" style={{ color: color }} />
              </div>
              {title}
            </CardTitle>
            <div className="flex items-center gap-3">
              <Badge className={`${statusColor} border`}>{data.status}</Badge>
              {gradeView === "comparison" ? (
                <div className="flex items-center gap-2">
                  <GradeCard grade={data.grade} size="sm" />
                  <span className="text-emerald-500">→</span>
                  <GradeCard grade={data.projected_grade} size="sm" />
                </div>
              ) : (
                <GradeCard grade={currentGrade} size="md" />
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Score Row with Mini Chart */}
          <div className="flex items-center gap-6 mb-6 p-4 rounded-xl" style={{ backgroundColor: `${color}08` }}>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">
                  {gradeView === "comparison" ? "Score Improvement" : "Section Score"}
                </span>
                {gradeView === "comparison" ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-slate-500">{data.score}%</span>
                    <span className="text-emerald-500">→</span>
                    <span className="text-2xl font-bold" style={{ color: color }}>{data.projected_score}%</span>
                    <span className="text-sm text-emerald-600 font-medium">(+{data.projected_score - data.score})</span>
                  </div>
                ) : (
                  <span className="text-2xl font-bold" style={{ color: color }}>{data.score}%</span>
                )}
              </div>
              {gradeView === "comparison" ? (
                <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="absolute h-full bg-slate-400 rounded-full"
                    style={{ width: `${data.score}%` }}
                  ></div>
                  <div 
                    className="absolute h-full rounded-full transition-all duration-1000"
                    style={{ width: `${data.projected_score}%`, backgroundColor: color, opacity: 0.7 }}
                  ></div>
                </div>
              ) : (
                <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${data.score}%`, backgroundColor: color }}
                  ></div>
                </div>
              )}
            </div>
            <div className="w-32 h-16">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={color} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke={color} 
                    strokeWidth={2}
                    fill={`url(#gradient-${id})`}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Details Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }}></span>
                Metrics
              </h4>
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                {Object.entries(data.details).slice(0, Math.ceil(Object.keys(data.details).length / 2)).map(([key, value]) => (
                  <DetailItem key={key} label={formatLabel(key)} value={value} color={color} />
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }}></span>
                Details
              </h4>
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                {Object.entries(data.details).slice(Math.ceil(Object.keys(data.details).length / 2)).map(([key, value]) => (
                  <DetailItem key={key} label={formatLabel(key)} value={value} color={color} />
                ))}
              </div>
            </div>
          </div>
          
          {/* Recommendations */}
          {data.recommendations && data.recommendations.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                Recommendations
              </h4>
              <div className="space-y-2">
                {data.recommendations.map((rec, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-3 rounded-xl transition-colors"
                    style={{ backgroundColor: `${color}08` }}
                  >
                    <div 
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <span className="text-xs font-bold" style={{ color: color }}>{index + 1}</span>
                    </div>
                    <span className="text-sm text-slate-700">{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Competitive Analysis */}
          {data.local_benchmark && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-blue-500" />
                Competitive Analysis
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Local Competition */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-blue-900">Local Competition</span>
                    <Badge className={`${
                      data.local_rank === "Above Average" ? "bg-green-100 text-green-700" :
                      data.local_rank === "Average" ? "bg-yellow-100 text-yellow-700" :
                      "bg-red-100 text-red-700"
                    }`}>{data.local_rank}</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{data.score}%</div>
                      <div className="text-xs text-blue-600">Your Score</div>
                    </div>
                    <div className="text-slate-400">vs</div>
                    <div>
                      <div className="text-2xl font-bold text-slate-600">{data.local_benchmark}%</div>
                      <div className="text-xs text-slate-500">Local Avg</div>
                    </div>
                  </div>
                </div>
                
                {/* National Competition */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-purple-900">National Ranking</span>
                    <Badge className={`${
                      data.national_rank?.includes("Top") ? "bg-green-100 text-green-700" :
                      data.national_rank === "Above Average" ? "bg-blue-100 text-blue-700" :
                      "bg-orange-100 text-orange-700"
                    }`}>{data.national_rank}</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{data.score}%</div>
                      <div className="text-xs text-purple-600">Your Score</div>
                    </div>
                    <div className="text-slate-400">vs</div>
                    <div>
                      <div className="text-2xl font-bold text-slate-600">{data.national_benchmark}%</div>
                      <div className="text-xs text-slate-500">National Avg</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Gap to Leader - Show gap but NOT sales opportunity */}
              {data.competitive_gap > 0 && (
                <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-amber-900">Gap to Local Leader: </span>
                      <span className="text-lg font-bold text-amber-600">{data.competitive_gap} points</span>
                    </div>
                    <div className="text-sm text-amber-700">
                      Room for improvement in this area
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default SectionCard;
